import React, { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { TextField, Tabs, Tab, Box, CircularProgress } from "@material-ui/core";
import { ModalV2 } from "components/Modal/ModalV2";
import { useForm } from "hooks/useForm";
import { useSearch } from "hooks/useSearch";
import {
  getUnitDispensers,
  addUnitDispenser,
  editUnitDispenser,
} from "redux/actions/adminUnits/adminUnitsActions";
import { getElectronicEquipmentTypes } from "redux/actions/electronicEquipment/electronicEquipmentActions";
import { ADMIN_UNIT_LOADINGS } from "redux/reducers/adminUnits/types";
import Add from "@mui/icons-material/Add";
import RegularButton from "components/CustomButtons/Button";
import { SubmitBtns } from "components/CustomButtons/SubmitBtns";
import { animations } from "helpers/animations";
import { EquipmentSelector, EMPTY_EQUIPMENT_VALUES } from "./EquipmentSelector";

const INITIAL_DISPENSER_VALUES = {
  consecutive: "",
  name: "",
  deviceId: "",
  uuidAws: "",
  ...EMPTY_EQUIPMENT_VALUES,
};

const DISPENSER_VALIDATIONS = {
  consecutive: { required: true },
  name: { required: true },
};

const TabPanel = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    style={{ paddingTop: 16, display: value === index ? "block" : "none" }}
  >
    {children}
  </div>
);

/**
 * Formulario individual de dispensador (ESTACION).
 * El GET /dispensers/detail ya trae el equipo resuelto, así que el prefill es
 * directo. La selección de equipo vive en EquipmentSelector.
 */
const DispenserTabContent = ({
  dispenser,
  draftKey,
  station,
  data,
  onSubmit,
  onRemove,
  loading,
}) => {
  const { setSnackbar } = useSearch();

  const { values, onChange, setValues, errors, validate } = useForm(
    INITIAL_DISPENSER_VALUES,
    DISPENSER_VALIDATIONS
  );

  const { consecutive, name, deviceId, uuidAws, equipment } = values;

  const isEdit = !!dispenser?.id;
  // El backend no libera el equipo si recibe idEquipment: null, así que un
  // equipo ya guardado se puede cambiar por otro, pero no quitar.
  const hasSavedEquipment = !!dispenser?.idEquipment;

  // Prefill directo desde el detalle del dispensador (equipo ya resuelto).
  // También se repite cuando el detalle se recarga con el equipo resuelto
  // (después de guardar, el POST/PUT no lo trae).
  useEffect(() => {
    if (!dispenser?.id) return;
    const hasEquipment = !!dispenser.idEquipment;
    setValues({
      consecutive: dispenser.consecutive ?? "",
      name: dispenser.name || "",
      deviceId: dispenser.deviceId || "",
      uuidAws: dispenser.uuidAws || "",
      idEquipmentType: hasEquipment
        ? {
            label: dispenser.equipmentType,
            value: dispenser.idEquipmentType,
            id: dispenser.idEquipmentType,
          }
        : null,
      equipment: hasEquipment
        ? {
            id: dispenser.idEquipment,
            idEquipmentType: dispenser.idEquipmentType,
            number: dispenser.equipmentNumber,
            code: dispenser.equipmentCode,
            serialNumber: dispenser.equipmentSerialNumber,
            status: dispenser.equipmentStatus,
            label: dispenser.equipmentNumber != null ? String(dispenser.equipmentNumber) : "",
          }
        : null,
      code: dispenser.equipmentCode || "",
      serialNumber: dispenser.equipmentSerialNumber || "",
      status: dispenser.equipmentStatus || "",
    });
  }, [dispenser?.id, dispenser?.idEquipment, dispenser?.equipmentNumber]);

  const handleSubmit = () => {
    validate([], () => {
      if (hasSavedEquipment && !equipment?.id) {
        setSnackbar(
          "El dispensador ya tiene un equipo asignado; selecciona un número para cambiarlo",
          "warning"
        );
        return;
      }
      const form = {
        consecutive,
        name,
        deviceId,
        uuidAws,
        idEquipment: equipment?.id || null,
      };
      onSubmit(form, dispenser?.id, draftKey);
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 15, width: "100%" }}>
      <TextField
        fullWidth
        label="Planta"
        value={station?.label || station?.name || ""}
        variant="outlined"
        disabled
      />

      <TextField
        fullWidth
        label="Unidad"
        value={data?.number || ""}
        variant="outlined"
        disabled
      />

      <TextField
        fullWidth
        label="Consecutivo"
        name="consecutive"
        value={consecutive}
        onChange={onChange}
        variant="outlined"
        error={!!errors.consecutive}
        helperText={errors.consecutive}
      />

      <TextField
        fullWidth
        label="Nombre"
        name="name"
        value={name}
        onChange={onChange}
        variant="outlined"
        error={!!errors.name}
        helperText={errors.name}
      />

      <TextField
        fullWidth
        label="Device ID"
        name="deviceId"
        value={deviceId}
        onChange={onChange}
        variant="outlined"
      />

      <TextField
        fullWidth
        label="UUID AWS"
        name="uuidAws"
        value={uuidAws}
        onChange={onChange}
        variant="outlined"
      />

      <EquipmentSelector
        values={values}
        onChange={setValues}
        station={station}
        idCurrent={dispenser?.idEquipment}
        lockEquipment={hasSavedEquipment}
      />

      {isEdit ? (
        <Box
          className={animations.fadeIn}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 20,
            gap: 8,
            animationDelay: ".5s",
          }}
        >
          <RegularButton color="info" onClick={handleSubmit} loading={loading}>
            Guardar
          </RegularButton>
        </Box>
      ) : (
        <SubmitBtns
          onClose={onRemove}
          onSubmit={handleSubmit}
          loading={loading}
          submitBtnText="Agregar"
          cancelColor="danger"
          cancelText="Quitar dispensador"
        />
      )}
    </div>
  );
};

/**
 * Modal de Dispensadores para unidades tipo ESTACION.
 * Tabs por dispensador existente (GET /dispensers/detail) + tabs de nuevos
 * dispensadores en creación mediante el botón "+".
 */
export const DispenserForm = ({ open, setOpen, data, station }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [draftKeys, setDraftKeys] = useState([]);
  // Tab que se está guardando: "saved-<id>" o "draft-<key>". SAVE_DISPENSER
  // es un solo flag, así que se usa esto para mostrar el loading solo ahí.
  const [savingKey, setSavingKey] = useState(null);

  const { dispensers, isLoadingComp } = useSelector(
    (state) => ({
      dispensers: state.adminUnits?.unitDispensers?.[data?.id] || [],
      isLoadingComp: state.search?.isLoadingComp,
    }),
    shallowEqual
  );

  const loadingGet = isLoadingComp?.[ADMIN_UNIT_LOADINGS.GET_DISPENSERS];
  const loadingSave = isLoadingComp?.[ADMIN_UNIT_LOADINGS.SAVE_DISPENSER];

  useEffect(() => {
    if (data?.id) dispatch(getUnitDispensers(data.id));
    dispatch(getElectronicEquipmentTypes());
  }, []);

  const removeDraft = useCallback(
    (draftKey) => {
      const updated = draftKeys.filter((k) => k !== draftKey);
      const total = dispensers.length + updated.length;
      setDraftKeys(updated);
      setActiveTab((current) => Math.min(current, Math.max(0, total - 1)));
    },
    [draftKeys, dispensers.length]
  );

  const handleSubmitDispenser = useCallback(
    (form, dispenserId, draftKey) => {
      setSavingKey(dispenserId ? `saved-${dispenserId}` : `draft-${draftKey}`);
      const isOnlyTab = dispensers.length + draftKeys.length === 1;

      const onSaved = async (response) => {
        if (isOnlyTab) {
          setOpen();
          return;
        }
        // Hay más pestañas: el modal se queda abierto para no perder lo capturado.
        // Se recarga el detalle sin el loading completo (desmontaría las pestañas)
        // porque el POST/PUT no trae el equipo resuelto.
        const list = await dispatch(getUnitDispensers(data.id, { silent: true }));
        if (!dispenserId) setDraftKeys((prev) => prev.filter((k) => k !== draftKey));
        const savedId = dispenserId || response?.id;
        const index = (list || []).findIndex((d) => d.id === savedId);
        if (index >= 0) setActiveTab(index);
      };

      if (dispenserId) {
        dispatch(editUnitDispenser(data.id, dispenserId, form, onSaved));
      } else {
        dispatch(addUnitDispenser(data.id, form, onSaved));
      }
    },
    [data?.id, dispensers.length, draftKeys.length, setOpen]
  );

  const handleAddTab = () => {
    const nextKey = draftKeys.length ? Math.max(...draftKeys) + 1 : 0;
    const updated = [...draftKeys, nextKey];
    setDraftKeys(updated);
    setActiveTab(dispensers.length + updated.length - 1);
  };

  return (
    <ModalV2
      open={open}
      onClose={setOpen}
      title={`Dispensadores — Unidad ${data?.number || ""}`}
      toRight
      width={500}
      isLoading={loadingGet}
    >
      <div style={{ width: "100%" }}>
        {loadingGet ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <CircularProgress size={30} />
          </div>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={(_, newVal) => setActiveTab(newVal)}
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                textColor="primary"
              >
                {dispensers.map((disp, idx) => (
                  <Tab
                    key={disp.id || `saved-${idx}`}
                    label={disp.name || `Dispensador ${idx + 1}`}
                  />
                ))}
                {draftKeys.map((key, idx) => (
                  <Tab key={`draft-${key}`} label={`Nuevo ${idx + 1}`} />
                ))}
                <Tab icon={<Add />} aria-label="Nuevo dispensador" onClick={handleAddTab} />
              </Tabs>
            </Box>

            {dispensers.map((disp, idx) => (
              <TabPanel key={disp.id || `saved-${idx}`} value={activeTab} index={idx}>
                <DispenserTabContent
                  dispenser={disp}
                  station={station}
                  data={data}
                  onSubmit={handleSubmitDispenser}
                  loading={loadingSave && savingKey === `saved-${disp.id}`}
                />
              </TabPanel>
            ))}

            {draftKeys.map((key, idx) => (
              <TabPanel
                key={`draft-${key}`}
                value={activeTab}
                index={dispensers.length + idx}
              >
                <DispenserTabContent
                  dispenser={null}
                  draftKey={key}
                  station={station}
                  data={data}
                  onSubmit={handleSubmitDispenser}
                  onRemove={() => removeDraft(key)}
                  loading={loadingSave && savingKey === `draft-${key}`}
                />
              </TabPanel>
            ))}

            {dispensers.length === 0 && draftKeys.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  padding: 4,
                  color: "text.secondary",
                }}
              >
                <span>No hay dispensadores. Agrega uno con el botón +</span>
              </Box>
            )}
          </>
        )}
      </div>
    </ModalV2>
  );
};
